import type { RemoteSlices } from './protocol'

/**
 * How each top-level store key travels from the host (the window connected
 * to the backend) to its replica, which renders the same state without
 * opening any connection.
 *
 * - `shared`: the host's value replaces the replica's.
 * - `local`: never sent; each window keeps its own (connection details, UI of
 *   that window, values that cannot be cloned).
 * - `partial`: sent without the listed fields; the replica keeps its own
 *   values for them.
 * - `merge`: sent whole; `merge` decides what the replica keeps.
 * - `relay`: sent once, in the snapshot; after that the host forwards the
 *   actions whose type starts with one of `actionPrefixes`, and the replica's
 *   own reducer applies them. For slices that are an event protocol whose
 *   reducer depends on this window's own flags (the media players' offset
 *   hand-shake), where copying state would be wrong.
 *
 * `SyncPolicy` maps every key, so adding a reducer without a decision does not
 * compile.
 */
export type SlicePolicy<S> =
	| { kind: 'shared' }
	| { kind: 'local' }
	| { kind: 'partial'; omit: ReadonlyArray<keyof S> }
	| { kind: 'merge'; merge: (local: S, host: S) => S }
	| { kind: 'relay'; actionPrefixes: ReadonlyArray<string> }

/** The store's keys; `combineReducers` adds a symbol brand, which is no slice. */
export type StateKey<S> = Exclude<keyof S, symbol>

export type SyncPolicy<S> = { [K in StateKey<S>]-?: SlicePolicy<S[K]> }

/**
 * The keys the host sends: in a snapshot every key but the local ones, in a
 * patch also not the relayed ones.
 */
export const syncedKeys = <S>(
	policy: SyncPolicy<S>,
	message: 'snapshot' | 'patch' = 'snapshot'
): Array<StateKey<S>> =>
	(Object.keys(policy) as Array<StateKey<S>>).filter(key => {
		const kind = policy[key].kind
		return kind !== 'local' && (message === 'snapshot' || kind !== 'relay')
	})

/**
 * Whether the host forwards an action of a type as it is - the policy read
 * once, since it is asked for every action the host's store applies.
 */
export const relayMatcher = <S>(
	policy: SyncPolicy<S>
): ((type: string) => boolean) => {
	const prefixes = (Object.keys(policy) as Array<StateKey<S>>).flatMap(key => {
		const slicePolicy = policy[key]
		return slicePolicy.kind === 'relay' ? slicePolicy.actionPrefixes : []
	})
	return type => prefixes.some(prefix => type.startsWith(prefix))
}

const withoutFields = <S>(
	slice: S,
	omit: ReadonlyArray<keyof S>
): Partial<S> => {
	const copy: Partial<S> = { ...slice }
	omit.forEach(field => delete copy[field])
	return copy
}

/**
 * What the host sends for `keys` of `state`: the slices themselves, or for
 * `partial` keys a copy without the omitted fields. `local` keys are skipped.
 */
export const projectForReplica = <S>(
	state: S,
	policy: SyncPolicy<S>,
	keys: ReadonlyArray<StateKey<S>>
): Record<string, unknown> => {
	const out: Record<string, unknown> = {}
	keys.forEach(key => {
		const slicePolicy = policy[key]
		switch (slicePolicy.kind) {
			case 'local':
				return
			case 'partial':
				out[String(key)] = withoutFields(state[key], slicePolicy.omit)
				return
			case 'shared':
			case 'merge':
			case 'relay':
				out[String(key)] = state[key]
				return
		}
	})
	return out
}

/**
 * The replica's state after the host sent `remote`. Keys the host did not
 * send keep their value, and so do `local` keys whatever was sent.
 */
export const applyFromHost = <S>(
	local: S,
	remote: RemoteSlices,
	policy: SyncPolicy<S>
): S => {
	const next: S = { ...local }
	let changed = false
	Object.keys(remote).forEach(name => {
		if (!Object.prototype.hasOwnProperty.call(policy, name)) {
			return
		}
		const key = name as StateKey<S>
		const slicePolicy = policy[key]
		// The host runs the same build (checked by the protocol), so a sent
		// value has the slice's own shape.
		const host = remote[name] as S[typeof key]
		switch (slicePolicy.kind) {
			case 'local':
				return
			case 'shared':
			case 'relay':
				next[key] = host
				break
			case 'partial':
				next[key] = { ...local[key], ...host }
				break
			case 'merge':
				next[key] = slicePolicy.merge(local[key], host)
				break
		}
		changed = true
	})
	return changed ? next : local
}
